/**
 * src/api/remediate.ts
 *
 * Frontend API client for the Scraper Remediator:
 *   - listRemediationCandidates
 *   - hotSwapExecutor
 *   - openRemediationPR
 *   - getAutoMergeStatus
 */

import { request } from './client';
import { listScrapers } from './observe';
import type { Executor, ScraperRecord } from '@/types';

export interface RemediationCandidate {
  id: string;
  displayName: string;
  executor: Executor;
  lastRunStatus: 'ok' | 'blocked' | 'error' | 'never';
  lastRunAt: string | null;
  botRate24h: number;
  savedPagesLastRun: number;
  runsPerDay: number;
  enabled: boolean;
  baseUrl: string | null;
  searchTerms: string[];
  lastAlert: { message: string; ts: string } | null;
}

export interface HotSwapResult {
  ok: true;
  scraperId: string;
  previousExecutor: Executor;
  newExecutor: Executor;
  updatedAt: string;
}

export interface ProbeSummaryItem {
  engine: string;
  ok: boolean;
  status?: number;
  durationMs?: number;
  blockVerdict?: 'ok' | 'soft_block' | 'hard_block';
}

export interface RemediationPrRequest {
  scraperId: string;
  targetExecutor: Executor;
  probeResultsSummary?: {
    matrix: ProbeSummaryItem[];
    recommendedEngine: string;
    reason?: string;
  };
  autoMergeOnGreenCi?: boolean;
}

export interface RemediationPrResult {
  ok: true;
  scraperId: string;
  prNumber: number;
  prUrl: string;
  branch: string;
  targetExecutor: Executor;
  autoMergeEnabled: boolean;
}

export interface AutoMergeStatus {
  ok: true;
  prNumber: number;
  ciStatus: 'pass' | 'fail' | 'pending' | 'none' | null;
  merged: boolean;
  mergedAt: string | null;
  state: 'open' | 'closed';
  autoMerged: boolean;
  message?: string;
}

function mapScraperToCandidate(s: ScraperRecord): RemediationCandidate {
  return {
    id: s.id,
    displayName: s.display_name || s.id,
    executor: s.executor,
    lastRunStatus: s.last_run_status ?? 'never',
    lastRunAt: s.last_run_at ?? null,
    botRate24h: s.bot_rate_24h ?? 0,
    savedPagesLastRun: s.saved_pages_last_run ?? 0,
    runsPerDay: s.runs_per_day ?? 0,
    enabled: s.enabled ?? true,
    baseUrl: s.base_url || s.default_base_url || null,
    searchTerms:
      s.search_terms && s.search_terms.length > 0
        ? s.search_terms
        : s.default_search_terms && s.default_search_terms.length > 0
          ? s.default_search_terms
          : ['iphone 15', 'ps5 console'],
    lastAlert: s.last_alert ? { message: s.last_alert.message, ts: s.last_alert.ts } : null,
  };
}

export async function listRemediationCandidates(opts: { blockedOnly?: boolean } = {}): Promise<RemediationCandidate[]> {
  const qs = opts.blockedOnly ? '?blockedOnly=true' : '';
  try {
    const res = await request<{ ok: true; candidates: RemediationCandidate[] }>(
      `/v1/observe/remediate/candidates${qs}`,
    );
    if (res?.candidates && res.candidates.length > 0) {
      return res.candidates;
    }
  } catch {
    // If backend route is pending deployment, fall back seamlessly to live scrapers API
  }

  try {
    const res = await listScrapers();
    const all = (res.scrapers ?? []).map(mapScraperToCandidate);
    if (opts.blockedOnly) {
      const blocked = all.filter(
        (c) => c.lastRunStatus === 'blocked' || c.lastRunStatus === 'error' || c.botRate24h > 0.4,
      );
      if (blocked.length > 0) return blocked;
    }
    return all;
  } catch (err) {
    console.error('Failed to list scrapers for remediation:', err);
    return [];
  }
}

export async function hotSwapExecutor(scraperId: string, targetExecutor: Executor): Promise<HotSwapResult> {
  return request<HotSwapResult>('/v1/observe/remediate/hot-swap', {
    method: 'POST',
    body: { scraperId, targetExecutor },
  });
}

export async function openRemediationPR(req: RemediationPrRequest): Promise<RemediationPrResult> {
  return request<RemediationPrResult>('/v1/observe/remediate/open-pr', {
    method: 'POST',
    body: req,
  });
}

export async function getAutoMergeStatus(prNumber: number, targetExecutor?: Executor): Promise<AutoMergeStatus> {
  const qs = targetExecutor ? `?targetExecutor=${encodeURIComponent(targetExecutor)}` : '';
  return request<AutoMergeStatus>(`/v1/observe/remediate/auto-merge-status/${prNumber}${qs}`);
}
